/**
 * Creature resolve chain (E2.3.S2) — turn an enemy entity's on-the-wire display
 * NAME into the asset-pack + blockout-proxy model the renderer should instantiate
 * on that entity's group.
 *
 * Unlike equipment (E2.2.S2, keyed by `ItemWire.contentId`), the enemy wire carries
 * NO contentId — only `kind:'enemy'` + `name` (`shared/src/protocol/messages.ts`;
 * the backend sets entity `name = EnemyDef.name`, `backend/src/world/from-content.ts`).
 * So the chain resolves by NAME through a client-side reverse map built from the
 * client-pack `enemies` projection (id/name/presentation — `client-pack.ts`
 * PRESENTATION_FIELDS): the AD-141 `RESOURCE_NODE_ASSET` client-convention pattern,
 * NOT a `shared/` constant and NOT a wire change (`EntityState` is untouched; an
 * enemy contentId is deliberately NOT added — the AD-156 seam, roadmap §4).
 *
 *   enemy EntityState.name
 *     → name→def reverse map        (display-name collision ⇒ null, deterministic)
 *       → EnemyDef.presentation      (assetPack + model)  [existing OPTIONAL field]
 *         → pack-qualified asset-packs[assetPack]          (the AssetPackDef manifest)
 *           → AssetLoader.instantiate(model, lod0)         (done by the renderer)
 *
 * Every unresolvable link returns `null` so the caller keeps the EXISTING cone+eye
 * placeholder (the graceful-fallback contract — the fallback must stay reachable).
 * Pack-membership of `model` is NOT asserted here: since AD-413 `validate.ts` proves
 * every enemy binds `assetPack` + `model` and that `model` is an asset of that pack
 * (the E2.3 "free string, never validate.ts" rule is superseded); the suite still
 * proves the RENDERER side — by-name uniqueness, on-disk .glb, budgets. Pure + node-safe (types only from the client surface). Import
 * boundary (AD-122): shared types via `@great-cloud/shared/client` only.
 */

import type { AssetPackDef, PresentationRef } from '@great-cloud/shared/client';

/** The projected enemy fields the reverse map reads (client-pack `enemies`). */
export interface EnemyPresentation {
  /** Display name — the ONLY enemy resolution key on the wire (`EntityState.name`). */
  name: string;
  /** Existing OPTIONAL presentation binding (assetPack + model), or undefined. */
  presentation?: PresentationRef;
  /**
   * The projected telegraphed attacks (client-pack `ENEMY_ATTACK_FIELDS`) — read
   * here only for `id` → `motion` (AD-636). Loose on purpose: a pack is data, and
   * a malformed entry degrades to "no motion", never a throw.
   */
  attacks?: readonly { id?: unknown; motion?: unknown }[];
}

/**
 * attackId → motion for ONE enemy, or `null` when the display name is unknown or
 * collided (the same deterministic no-binding as `EnemyNameMap`). An attack with
 * no `motion` is simply absent from the inner map.
 */
export type EnemyAttackMotionMap = ReadonlyMap<string, ReadonlyMap<string, string> | null>;

/**
 * Build the name → (attackId → motion) map from the client-pack `enemies`
 * projection (AD-636). The wire `telegraph` names its attack by `attackId`, which
 * is unique only WITHIN an enemy, so it resolves against the def the entity is
 * already bound to by name — exactly the chain `buildEnemyNameMap` uses for the
 * mesh. The motion is not validated here: the content validator owns the
 * vocabulary (AD-028), and a motion no rig clip answers to falls back anyway.
 */
export function buildEnemyAttackMotionMap(
  enemies: readonly EnemyPresentation[],
): EnemyAttackMotionMap {
  const map = new Map<string, ReadonlyMap<string, string> | null>();
  for (const def of enemies) {
    const name = def?.name;
    if (typeof name !== 'string' || name === '') continue;
    if (map.has(name)) {
      map.set(name, null); // collision ⇒ deterministic no-binding
      continue;
    }
    const motions = new Map<string, string>();
    for (const attack of Array.isArray(def.attacks) ? def.attacks : []) {
      const id = attack?.id;
      const motion = attack?.motion;
      if (typeof id === 'string' && typeof motion === 'string' && motion !== '') {
        motions.set(id, motion);
      }
    }
    map.set(name, motions);
  }
  return map;
}

/**
 * How the renderer reads creature presentation data — the client content pack's
 * `enemies` and `asset-packs` projections. Built in App from the loaded
 * `ContentPackStore` (`Object.values(pack.categories.enemies)` / `get('asset-packs', id)`);
 * a small test fake satisfies it too. `enemies()` is a LIVE read because the pack
 * loads after `Game` constructs, so the reverse map is built lazily on first use.
 * Kept separate from the scene's `SceneContentLookup` + equipment's `EquipmentContent`
 * so this feature touches no other module's contract.
 */
export interface CreatureContent {
  /** All projected enemy defs (the client-pack `enemies` category values). */
  enemies(): readonly EnemyPresentation[];
  /** Client-pack `asset-packs[id]` (its `assets[]` manifest), or undefined if not delivered. */
  assetPack(id: string): AssetPackDef | undefined;
  /**
   * Identity of the pack `enemies()` reads (App: the store's content hash). A reconnect
   * onto a new hash swaps the pack, and `enemies()` returns a fresh array per call, so
   * array identity cannot say "the pack changed" — this can. Omitted ⇒ build once.
   */
  revision?(): string;
}

/** A resolved creature attachment: everything the renderer needs to place a proxy. */
export interface CreatureResolution {
  /** The resolved asset-pack id (`presentation.assetPack`). */
  assetPack: string;
  /** The proxy asset id within the pack to instantiate at lod0 (`presentation.model`). */
  model: string;
  /** The resolved pack def — construct an `AssetManifest`/`AssetLoader` from it. */
  pack: AssetPackDef;
}

/**
 * A prebuilt name → presentation reverse map. A display name shared by ≥2 defs
 * maps to `null` — a DETERMINISTIC no-binding (never an arbitrary winner), so a
 * runtime lookup of a collided name falls back to the cone. A name whose def has
 * no presentation also maps to `null`. Absent name ⇒ unknown ⇒ cone. (S1's
 * name-uniqueness check makes real collisions a content error; the map still
 * degrades safely if one ever slips in.)
 */
export type EnemyNameMap = ReadonlyMap<string, PresentationRef | null>;

/**
 * Build the name→presentation reverse map from the client-pack `enemies`
 * projection. Exported per the story: `entity.name` is the only resolution key on
 * the wire, so this map IS the enemy→mesh chain's client side.
 */
export function buildEnemyNameMap(enemies: readonly EnemyPresentation[]): EnemyNameMap {
  const map = new Map<string, PresentationRef | null>();
  for (const def of enemies) {
    const name = def?.name;
    if (typeof name !== 'string' || name === '') continue;
    if (map.has(name)) {
      map.set(name, null); // collision ⇒ deterministic no-binding (both/all lose)
      continue;
    }
    map.set(name, def.presentation ?? null);
  }
  return map;
}

/**
 * Resolve an enemy entity's display name to its proxy pack + model via a prebuilt
 * reverse map, or `null` for any graceful fallback: name absent/collided in the
 * map, no presentation binding, or a pack that isn't delivered. The final
 * `instantiate(model, lod0)` — and its own null fallback for an unknown model /
 * load error — is the renderer's/loader's job (⇒ the cone stays).
 */
export function resolveCreature(
  nameMap: EnemyNameMap,
  content: Pick<CreatureContent, 'assetPack'>,
  name: string | null | undefined,
): CreatureResolution | null {
  if (name == null || name === '') return null;
  if (!nameMap.has(name)) return null; // unknown name ⇒ cone
  const presentation = nameMap.get(name);
  if (presentation == null) return null; // collided name OR def has no presentation ⇒ cone
  const assetPack = presentation.assetPack;
  const model = presentation.model;
  if (assetPack === undefined || assetPack === '' || model === undefined || model === '') {
    return null; // presentation present but not a usable binding ⇒ cone
  }
  const pack = content.assetPack(assetPack);
  if (pack === undefined) return null; // pack not delivered ⇒ cone
  return { assetPack, model, pack };
}
